import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  AddBoxOutlined,
  AutorenewOutlined,
  Close,
  SearchOutlined,
} from "@mui/icons-material";
import PaginationControl from "components/shared/modal/PaginationControl";
import { COLUMNS, Trips } from "./types/types";
import IconTextButton from "components/shared/buttons/IconTextButton";
import TripDetailsModal from "./component/TripDetailsModal";
import {
  TRIP_STATUS,
  format,
  getStatusColor,
} from "utils/index";
import { useNavigate } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "store";
import { fetchTrips, forceRefreshTrips } from "slice/tripSlice";

const CreateTripScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [page, setPage] = useState(1);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTrips, setSelectedTrip] = useState<Trips | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const tripCache = useSelector((state: RootState) => state.trips.tripCache);
  const [status, setStatus] = useState<string>(TRIP_STATUS.ImagesSlot_Pending);

  const userDetails = useSelector(
    (state: RootState) => state.appLayout.userDetails
  ) as any;

  const handleChangePage = (
    event: React.ChangeEvent<unknown> | null,
    page: number
  ) => {
    setPage(page);
  };

  const handleRowClick = (row: Trips) => {
    setSelectedTrip(row);
    setOpenDialog(true);
  };

  const displayedTrips = tripCache[status] || [];

  const filteredTrips = displayedTrips.filter((trip) =>
    [trip.title, trip.group, trip.tripType].some((field) =>
      field.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
    if (tripCache[newStatus] === null) {
      dispatch(fetchTrips({ tripStatus: newStatus, id: userDetails?.userId }));
    }
  };
  const handleRefreshData = () => {
    setOpenDialog(false);
    dispatch(forceRefreshTrips(status));
    dispatch(fetchTrips({ tripStatus: status, id: userDetails?.userId }));
  };
  useEffect(() => {
    if (tripCache[status] === null) {
      dispatch(fetchTrips({ tripStatus: status, id: userDetails?.userId }));
    }
  }, []);

  // Determine which columns to show based on screen size
  const getVisibleColumns = () => {
    if (isMobile) {
      return COLUMNS.filter((col) =>
        ["title", "tripApprovalStatus"].includes(col.id)
      );
    } else if (isTablet) {
      return COLUMNS.filter((col) =>
        [
          "tripId",
          "title",
          "groupType",
          "tripType",
          "slotCount",
          "tripApprovalStatus",
          "updatedAt",
        ].includes(col.id)
      );
    }
    return COLUMNS;
  };

  const visibleColumns = getVisibleColumns();

  return (
    <Box>
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "flex-start" : "center"}
        mb={3}
        spacing={2}
      >
        <Typography variant="h5" fontWeight="600" color="#181D27">
          Trips created
        </Typography>
        <TextField
          id="search-field"
          variant="outlined"
          placeholder={
            isMobile
              ? "Search trips"
              : "Search trips by title, group, trip type"
          }
          value={searchTerm}
          fullWidth
          sx={{
            flex: 1,
            marginX: isMobile ? 0 : 2,
            borderRadius: "50px",
            transition: "all 0.3s ease-in-out",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.23)"
                    : "rgba(0, 0, 0, 0.23)",
                borderRadius: "50px",
                height: isMobile ? "50px" : "60px",
              },
              "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
          slotProps={{
            input: {
              "aria-label": "Search trips",
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ color: "#757575", mr: 1 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment
                  onClick={() => {
                    setSearchTerm("");
                    document.getElementById("search-field")?.focus();
                  }}
                  position="end"
                  sx={{
                    cursor: "pointer",
                    display: searchTerm ? "flex" : "none",
                    alignItems: "center",
                    mr: 1,
                  }}
                >
                  <Close sx={{ color: "#757575" }} />
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <IconTextButton
          variant="contained"
          label={isMobile ? "New Trip" : "Create new trip"}
          Icon={
            <AddBoxOutlined
              sx={{
                color: "#414651",
                fontSize: "20px",
                mr: 1,
              }}
            />
          }
          onClick={() => navigate("/create-new-trips")}
        />
      </Stack>

      <Paper
        sx={{
          width: "100%",
          mb: 2,
          borderRadius: "12px",
          overflow: "hidden",
          boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.05)",
        }}
      >
        <Box
          p={isMobile ? 1.5 : 2}
          borderBottom={1}
          borderColor="divider"
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="flex-start"
          alignItems={isMobile ? "flex-start" : "center"}
          gap={1}
        >
          <Typography variant="subtitle1" fontWeight="600">
            Trips Status{" "}
          </Typography>
          <Box
            display="flex"
            gap={1}
            p={isMobile ? 1 : 2}
            flexWrap="wrap"
            sx={{
              maxWidth: "100%",
              overflowX: isMobile ? "auto" : "visible",
              pb: isMobile ? 1 : 2,
            }}
          >
            {Object.values(TRIP_STATUS).map((tripStatus) => (
              <Chip
                key={tripStatus}
                label={tripStatus}
                onClick={() => handleStatusChange(tripStatus)}
                color={status === tripStatus ? "primary" : "default"}
                variant={status === tripStatus ? "filled" : "outlined"}
                sx={{
                  cursor: "pointer",
                  borderRadius: "8px",
                  fontWeight: status === tripStatus ? 600 : 400,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                  },
                }}
              />
            ))}
          </Box>
          <IconTextButton
            variant="contained"
            label={isMobile ? "Refresh" : "Refresh Data"}
            sx={{
              marginLeft: isMobile ? 0 : "auto",
              marginTop: isMobile ? 1 : 0,
            }}
            Icon={
              <AutorenewOutlined
                sx={{
                  color: "#414651",
                  fontSize: "20px",
                  mr: 1,
                  animation: `${
                    tripCache[status] === null
                      ? "spin 1s linear infinite"
                      : "none"
                  }`,
                  "@keyframes spin": {
                    "0%": {
                      transform: "rotate(0deg)",
                    },
                    "100%": {
                      transform: "rotate(360deg)",
                    },
                  },
                }}
              />
            }
            onClick={handleRefreshData}
          />
        </Box>

        <TableContainer sx={{ maxHeight: "calc(100vh - 300px)" }}>
          <Table
            stickyHeader
            sx={{ minWidth: isMobile ? 350 : 700 }}
            aria-label="Trip table"
            size={isMobile ? "small" : "medium"}
          >
            <TableHead>
              <TableRow>
                {visibleColumns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align ?? "left"}
                    style={{
                      minWidth: column.minWidth,
                      padding: isMobile ? "10px" : "14px",
                    }}
                    sx={{
                      color: "#717680",
                      fontWeight: "600",
                      fontSize: isMobile ? "13px" : "14px",
                      bgcolor: "#FAFAFA",
                      fontFamily: "Inter",
                      position: "sticky",
                      zIndex: 100,
                      boxShadow:
                        column.id === "title"
                          ? "2px 0px 3px rgba(0,0,0,0.05)"
                          : "none",
                      ...column.sx,
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredTrips
                ?.sort((a, b) => {
                  const dateA = a.createdAt
                    ? new Date(a.createdAt)
                    : new Date(0);
                  const dateB = b.createdAt
                    ? new Date(b.createdAt)
                    : new Date(0);
                  return dateB.getTime() - dateA.getTime();
                })
                ?.slice((page - 1) * 10, page * 10)
                ?.map((row, index) => (
                  <TableRow
                    key={row.id}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                      "&:nth-of-type(even)": {
                        backgroundColor: "rgba(0, 0, 0, 0.02)",
                      },
                      transition: "background-color 0.2s ease",
                    }}
                    onClick={() => handleRowClick(row)}
                  >
                    {visibleColumns.map((column) => {
                      if (column.id === "title") {
                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              color: "#535862",
                              fontWeight: 500,
                              position: "sticky",
                              textAlign: "center",
                              zIndex: 1,
                              backgroundColor:
                                index % 2 === 0
                                  ? "white"
                                  : "rgba(0, 0, 0, 0.02)",
                              "&:hover": {
                                backgroundColor: "#f5f5f5",
                              },
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: isMobile ? "120px" : "200px",
                              padding: isMobile ? "10px" : "16px",
                            }}
                          >
                            {row?.title}
                          </TableCell>
                        );
                      } else if (column.id === "intro") {
                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              color: "#535862",
                              fontWeight: 400,
                              textAlign: "left",
                              maxWidth: "300px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              padding: isMobile ? "10px" : "16px",
                            }}
                          >
                            <Typography
                              component="div"
                              sx={{
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                lineHeight: "1.4em",
                                maxHeight: "2.8em",
                              }}
                            >
                              {row?.intro}
                            </Typography>
                          </TableCell>
                        );
                      } else if (column.id === "tripApprovalStatus") {
                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              color: "#535862",
                              fontWeight: 400,
                              minWidth: isMobile ? "90px" : "100px",
                              padding: isMobile ? "8px" : "10px",
                            }}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              sx={{
                                textTransform: "none",
                                backgroundColor:
                                  getStatusColor(row?.tripApprovalStatus)
                                    .bgcolor || "#F3F4F6",
                                border: "1px solid",
                                borderColor:
                                  getStatusColor(row?.tripApprovalStatus)
                                    .borderColor || "#D1D5DB",
                                width: isMobile ? "90px" : "100px",
                                height: isMobile ? "36px" : "40px",
                                borderRadius: "8px",
                                color:
                                  getStatusColor(row?.tripApprovalStatus)
                                    .textcolor || "#4B5563",
                                fontSize: isMobile ? "11px" : "12px",
                                fontWeight: 500,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                margin: "0 auto",
                                gap: "6px",
                                transition:
                                  "transform 0.2s ease, box-shadow 0.2s ease",
                                "&:hover": {
                                  transform: "translateY(-2px)",
                                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                  backgroundColor:
                                    getStatusColor(row?.tripApprovalStatus)
                                      .bgcolor || "#F3F4F6",
                                },
                                "@media (max-width: 600px)": {
                                  width: "90px",
                                  fontSize: "11px",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: "10px",
                                  height: "10px",
                                  borderRadius: "50%",
                                  backgroundColor:
                                    getStatusColor(row?.tripApprovalStatus)
                                      .color || "#9CA3AF",
                                  boxShadow: `0 0 6px ${
                                    getStatusColor(row?.tripApprovalStatus)
                                      .color || "#9CA3AF"
                                  }`,
                                }}
                              />
                              {row?.tripApprovalStatus || "N/A"}
                            </Button>
                          </TableCell>
                        );
                      } else {
                        return (
                          <TableCell
                            key={column.id}
                            sx={{
                              color: "#535862",
                              fontWeight: 400,
                              textAlign: "center",
                              padding: isMobile ? "10px" : "16px",
                            }}
                          >
                            {column.id === "group"
                              ? row.group
                              : column.id === "tripType"
                              ? row.tripType
                              : column.id === "slotCount"
                              ? row.slotCount ?? 0
                              : column.id === "updatedAt"
                              ? format(row?.updatedAt)
                              : column.id === "tripId"
                              ? row.searchIdKey + row.searchId
                              : "N/A"}
                          </TableCell>
                        );
                      }
                    })}
                  </TableRow>
                ))}
              {filteredTrips?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumns.length + 1}
                    sx={{
                      textAlign: "center",
                      padding: "40px 0",
                      color: "#6B7280",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography variant="h6">No Trips available</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Try changing the status filter or search term
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <PaginationControl
            totalRows={filteredTrips?.length}
            page={page}
            onPageChange={handleChangePage}
          />
        </TableContainer>
      </Paper>
      {selectedTrips && (
        <TripDetailsModal
          open={openDialog}
          handleClose={() => setOpenDialog(false)}
          tripData={selectedTrips as Trips}
          handleRefetch={handleRefreshData}
        />
      )}
    </Box>
  );
};

export default CreateTripScreen;
